/**
 * Máquina de estados de los avisos de conexión de la teleconsulta (propuesta
 * «Avisos de conexión en la teleconsulta»).
 *
 * Esta pieza es LÓGICA PURA a propósito: no toca LiveKit, ni React, ni el DOM. Recibe en cada
 * muestreo qué señales están mal y devuelve qué debe ver la persona. Así se puede probar el
 * comportamiento —que un corte de 5 s no muestre nada, que el aviso no parpadee— sin levantar
 * una videollamada.
 *
 * COPIA ESPEJO: existe un archivo equivalente en el repositorio de la app móvil
 * (`MedisysPdP/src/utils/avisoConexion.ts`). Cualquier cambio de umbrales o de reglas se aplica
 * en ambos: el paciente y el médico deben ver lo mismo al mismo tiempo.
 *
 * Los peldaños:
 *
 *   0 – 8 s   el sistema se recupera solo, en silencio          → 'ninguno'
 *   8 – 25 s  aviso discreto, sin pedir nada                    → 'leve'
 *   > 25 s    con el audio vivo: seguir la consulta sin video   → 'soloAudio'
 *   > 25 s    sin audio: instrucción explícita con acción       → 'grave'
 *
 * POR QUÉ EXISTE 'soloAudio' (10-sep-2026). Antes solo había 'grave', así que un video
 * congelado mientras la conversación seguía perfectamente audible mostraba «Problemas de
 * conexión — use el botón para volver a conectarse». Dos cosas mal: alarma cuando la consulta
 * está funcionando, y sobre todo ACONSEJA LO PEOR — reconectar interrumpe un audio que estaba
 * bien. Ese día una paciente tocó «Reintentar» 29 veces en 45 segundos y terminó expulsada dos
 * veces de la sala. En una teleconsulta el audio es el canal esencial y el video es deseable:
 * si el audio fluye, lo correcto es decirlo con calma y seguir.
 */

/** Qué señal está fallando. Se muestra UNA sola aunque fallen varias (regla «un aviso a la vez»). */
export type SenalConexion =
  /** El canal con la sala se cayó o está reconectando. Señal dura: cuenta de inmediato. */
  | 'corte'
  /** La plataforma reporta calidad mala o perdida para nuestro propio participante. */
  | 'calidadMala'
  /**
   * El otro publica su cámara y no está muteada, pero aquí no hay pista: o no llegamos a
   * suscribirnos, o la suscripción se perdió. Es «publicado pero no me llega», que es distinto
   * de «se quedó pegado» y de «apagó la cámara». Se añadió el 04-09-2026: la primera versión
   * del detector devolvía «no opino» en este caso y la falla pasaba en silencio.
   */
  | 'videoAusente'
  /** Igual con el audio: publicado, sin mutear, y sin pista de este lado. */
  | 'audioAusente'
  /** Llega el video del otro pero los fotogramas dejaron de avanzar («se quedó pegado»). */
  | 'videoCongelado'
  /** La pista de audio del otro está activa pero no llegan datos. */
  | 'audioMudo'
  /** Nuestra propia cámara o micrófono se soltó y no se está publicando. */
  | 'medioPropioCaido'
  /**
   * La otra persona está en la sala y no publica NADA: ni audio ni video. Quien participa en
   * una videollamada siempre publica audio, así que esto no es una decisión suya —como apagar
   * la cámara— sino algo roto.
   *
   * Se añadió el 04-09-2026, tras un caso en que al médico lo expulsaron de la sala por
   * identidad duplicada, volvió a entrar sin publicar nada y estuvo tres minutos mudo e
   * invisible. `videoAusente` no lo cubría: sin publicación no hay nada que declarar ausente.
   */
  | 'contraparteMuda';

export type NivelAviso = 'ninguno' | 'leve' | 'soloAudio' | 'grave';

/**
 * Señales que siguen siendo GRAVES aunque llegue audio, porque reconectar sí es lo indicado:
 *
 *  - `corte` / `medioPropioCaido`: sin canal, o con nuestro propio micrófono caído, la otra
 *    persona no nos oye. Seguir «solo con audio» no es una opción: no hay audio que seguir.
 *  - `audioAusente` / `audioMudo`: el problema ES el audio.
 *
 * Todo lo demás —video congelado, video ausente, calidad mala— degrada a 'soloAudio' cuando
 * se confirma que el audio de la otra persona sigue llegando.
 */
const SENALES_QUE_EXIGEN_RECONECTAR: ReadonlySet<SenalConexion> = new Set<SenalConexion>([
  'corte',
  'medioPropioCaido',
  'audioAusente',
  'audioMudo',
]);

/**
 * Prioridad al elegir qué contar cuando fallan varias cosas a la vez —lo habitual—. Primero lo
 * que explica mejor el síntoma: un corte lo explica todo, y un video congelado es más concreto
 * que «calidad mala».
 */
const PRIORIDAD: SenalConexion[] = [
  'corte',
  'medioPropioCaido',
  // Que la otra persona no mande nada lo explica todo de su lado.
  'contraparteMuda',
  // Que no llegue nada explica mejor el síntoma que un video pegado, y un video pegado
  // explica mejor que «calidad mala».
  'videoAusente',
  'audioAusente',
  'videoCongelado',
  'audioMudo',
  'calidadMala',
];

/** Señales que no necesitan sostenerse para contar: o hay canal o no lo hay. */
const SENALES_DURAS: ReadonlySet<SenalConexion> = new Set<SenalConexion>(['corte']);

export const UMBRAL_LEVE_MS = 8_000;
export const UMBRAL_GRAVE_MS = 25_000;
/** Una señal blanda debe mantenerse este tiempo seguido antes de considerarse un problema. */
export const PERSISTENCIA_MS = 3_000;
/**
 * `contraparteMuda` necesita más margen que el resto: entre que alguien entra a la sala y
 * publica sus pistas pasan unos segundos legítimos, y ese hueco no es una falla.
 */
export const PERSISTENCIA_CONTRAPARTE_MS = 8_000;

/** Persistencia por señal; las que no están aquí usan `PERSISTENCIA_MS`. */
const PERSISTENCIA_POR_SENAL: Partial<Record<SenalConexion, number>> = {
  contraparteMuda: PERSISTENCIA_CONTRAPARTE_MS,
};
/** El aviso no se retira al primer instante de mejora, sino tras este tiempo estable. */
export const HISTERESIS_MS = 4_000;
/** Los primeros segundos de una llamada son irregulares por diseño: ahí nunca se avisa. */
export const GRACIA_INICIO_MS = 6_000;

export interface EstadoAvisoConexion {
  nivel: NivelAviso;
  /** Señal que se está contando, o null si no hay problema. */
  senal: SenalConexion | null;
  /** Milisegundos que lleva el problema, para poder mostrarlos y para las pruebas. */
  duracionMs: number;
}

/** Estado interno de la máquina. Lo crea `crearDetectorConexion`. */
interface Memoria {
  /** Cuándo se estableció la llamada (para la gracia de arranque). */
  inicio: number;
  /** Por señal blanda: desde cuándo está mal de forma continua. */
  desdeSenal: Partial<Record<SenalConexion, number>>;
  /** Desde cuándo hay un problema confirmado (null = todo bien). */
  problemaDesde: number | null;
  /** Señal que se está contando en el problema en curso. */
  senalActual: SenalConexion | null;
  /** Desde cuándo dejó de haber problema (para la histéresis). */
  sanoDesde: number | null;
}

/** Lo que el muestreo sabe además de qué está fallando. */
export interface ContextoConexion {
  /**
   * ¿Se confirmó que el audio de la otra persona SIGUE LLEGANDO? Es una confirmación positiva,
   * no la ausencia de una queja: `undefined` significa «no se pudo comprobar» (no hay pista, o
   * es la primera muestra) y en ese caso no se degrada a 'soloAudio', porque no consta que
   * quede algo utilizable.
   */
  audioRecibiendose?: boolean;
}

export interface DetectorConexion {
  /**
   * Alimenta la máquina con las señales que están mal AHORA y devuelve qué mostrar.
   * `ahora` se pasa explícito para poder probar el paso del tiempo sin esperarlo.
   */
  evaluar(
    malas: readonly SenalConexion[],
    ahora: number,
    contexto?: ContextoConexion,
  ): EstadoAvisoConexion;
}

const SIN_AVISO: EstadoAvisoConexion = { nivel: 'ninguno', senal: null, duracionMs: 0 };

/**
 * Crea la máquina. `inicio` es el instante en que la llamada quedó conectada; durante
 * `GRACIA_INICIO_MS` a partir de ahí no se avisa nada.
 */
export function crearDetectorConexion(inicio: number): DetectorConexion {
  const m: Memoria = {
    inicio,
    desdeSenal: {},
    problemaDesde: null,
    senalActual: null,
    sanoDesde: null,
  };

  return {
    evaluar(malas, ahora, contexto) {
      const conjunto = new Set(malas);

      // 1. Persistencia: una señal blanda solo cuenta si lleva PERSISTENCIA_MS seguidos mal.
      const confirmadas: SenalConexion[] = [];
      for (const senal of PRIORIDAD) {
        if (!conjunto.has(senal)) {
          delete m.desdeSenal[senal];
          continue;
        }
        if (SENALES_DURAS.has(senal)) {
          confirmadas.push(senal);
          continue;
        }
        const desde = m.desdeSenal[senal] ?? ahora;
        m.desdeSenal[senal] = desde;
        const minimo = PERSISTENCIA_POR_SENAL[senal] ?? PERSISTENCIA_MS;
        if (ahora - desde >= minimo) confirmadas.push(senal);
      }

      // 2. Arranque: mientras se establece la conexión no se avisa, y tampoco se empieza a
      //    contar — si no, al salir de la gracia el aviso aparecería de golpe ya escalado.
      if (ahora - m.inicio < GRACIA_INICIO_MS) {
        m.problemaDesde = null;
        m.senalActual = null;
        m.sanoDesde = null;
        return SIN_AVISO;
      }

      // 3. Apertura o cierre del problema, con histéresis al cerrar.
      if (confirmadas.length > 0) {
        m.sanoDesde = null;
        if (m.problemaDesde === null) m.problemaDesde = ahora;
        // La señal mostrada puede cambiar dentro del mismo problema (p. ej. un corte que
        // se resuelve pero deja el video congelado): se sigue contando desde el inicio.
        m.senalActual = confirmadas[0];
      } else if (m.problemaDesde !== null) {
        if (m.sanoDesde === null) m.sanoDesde = ahora;
        if (ahora - m.sanoDesde >= HISTERESIS_MS) {
          m.problemaDesde = null;
          m.senalActual = null;
          m.sanoDesde = null;
          return SIN_AVISO;
        }
      }

      if (m.problemaDesde === null || m.senalActual === null) return SIN_AVISO;

      const duracionMs = ahora - m.problemaDesde;
      let nivel: NivelAviso =
        duracionMs >= UMBRAL_GRAVE_MS ? 'grave'
        : duracionMs >= UMBRAL_LEVE_MS ? 'leve'
        : 'ninguno';

      /* Al llegar al peldaño de instrucción, la pregunta deja de ser «cuánto lleva mal» y pasa
         a ser «¿queda algo con lo que seguir?». Si el audio de la otra persona sigue llegando y
         lo que falla es el video, la consulta puede continuar: eso no se anuncia como avería
         sino como un modo, y sin proponer una reconexión que cortaría el audio que sí sirve. */
      if (
        nivel === 'grave' &&
        contexto?.audioRecibiendose === true &&
        m.senalActual !== null &&
        !SENALES_QUE_EXIGEN_RECONECTAR.has(m.senalActual)
      ) {
        nivel = 'soloAudio';
      }

      return { nivel, senal: nivel === 'ninguno' ? null : m.senalActual, duracionMs };
    },
  };
}

/** Texto del aviso discreto (peldaño 8–25 s). Igual en portal y app. */
export const TEXTO_LEVE = {
  titulo: 'Conexión inestable',
  detalle: 'Estamos teniendo problemas para mantener el audio o el video. Intentando restablecer…',
};

/**
 * Texto del modo solo audio. Deliberadamente NO dice «error» ni «problema»: describe un modo de
 * trabajo y confirma que la consulta sigue. Quien lo lee está hablando con su médico y necesita
 * saber dos cosas —que lo siguen oyendo, y que no tiene que hacer nada— no un diagnóstico de red.
 *
 * `paraMedico` cambia el sujeto: al médico hay que decirle por qué dejó de ver a su paciente,
 * para que no lo interprete como que se fue.
 */
export function textoSoloAudio(paraMedico = false): {
  titulo: string;
  detalle: string;
  accion: string;
} {
  return paraMedico
    ? {
        titulo: 'Consulta en audio',
        detalle: 'La conexión del paciente no da para video en este momento. Lo sigue escuchando '
          + 'con normalidad y puede continuar la consulta; la imagen volverá sola si su señal mejora.',
        accion: 'Reintentar el video',
      }
    : {
        titulo: 'Continuando solo con audio',
        detalle: 'Su conexión no alcanza para el video en este momento, pero el audio funciona bien: '
          + 'el médico lo escucha y usted lo escucha a él. Puede seguir la consulta con normalidad. '
          + 'La imagen volverá sola si la señal mejora.',
        accion: 'Reintentar el video',
      };
}

/**
 * Texto de la instrucción (peldaño > 25 s). El detalle se adapta a la señal para que la
 * persona reconozca lo que está viendo; la acción propuesta es siempre la misma.
 */
export function textoGrave(senal: SenalConexion | null): { titulo: string; detalle: string } {
  const titulo = 'Problemas de conexión';
  switch (senal) {
    case 'contraparteMuda':
      return { titulo, detalle: 'La otra persona está conectada pero no está enviando audio ni video. Use el botón para volver a conectarse.' };
    case 'videoAusente':
      return { titulo, detalle: 'No está llegando el video de la otra persona. Use el botón para volver a conectarse.' };
    case 'audioAusente':
      return { titulo, detalle: 'No está llegando el audio de la otra persona. Use el botón para volver a conectarse.' };
    case 'videoCongelado':
      return { titulo, detalle: 'La imagen se quedó congelada. Use el botón para volver a conectarse.' };
    case 'audioMudo':
      return { titulo, detalle: 'Dejó de llegar el audio. Use el botón para volver a conectarse.' };
    case 'medioPropioCaido':
      return { titulo, detalle: 'Su cámara o su micrófono dejaron de enviarse. Use el botón para volver a conectarse.' };
    default:
      return {
        titulo,
        detalle: 'No logramos restablecer el audio o el video. Si no puede escuchar o ver a la otra persona, '
          + 'o la imagen está congelada, use el botón para volver a conectarse.',
      };
  }
}

/**
 * ¿Es esta identidad la de una persona? En la sala también están el agente de transcripción
 * (`agent-…`) y los participantes de grabación (`EG_…`), que no publican cámara ni micrófono.
 *
 * Tomar «el primer participante remoto» —como hacía la primera versión— es una moneda al aire:
 * el agente entra antes que el médico, y el 04-09-2026 cuatro de cinco diagnósticos estaban
 * describiendo al agente y afirmando que el médico no publicaba cámara.
 */
export function esPersona(identidad: string | undefined | null): boolean {
  if (!identidad) return false;
  return !identidad.startsWith('agent-') && !identidad.startsWith('EG_');
}

/**
 * Elige a la contraparte humana de la llamada. Prefiere el prefijo esperado
 * (`medico_` para el paciente, `paciente_` para el médico) y, si no aparece, cae a la primera
 * identidad que sea de una persona. Nunca devuelve al agente ni a la grabación.
 */
export function elegirContraparte<T extends { identity?: string }>(
  participantes: readonly T[],
  prefijo: 'medico_' | 'paciente_',
): T | undefined {
  const personas = participantes.filter((p) => esPersona(p.identity));
  return personas.find((p) => p.identity?.startsWith(prefijo)) ?? personas[0];
}
